
import React, { useState, useEffect } from 'react'
import { Bell, AlertTriangle, Package, Clock, X } from 'lucide-react'
import { useCompras } from '../hooks/useCompras'

const NotificationBellCompras: React.FC = () => {
  const { notificacoes, markNotificationAsRead } = useCompras()
  const [isOpen, setIsOpen] = useState(false)

  const unreadNotifications = notificacoes.filter(n => !n.lida)

  // Get icon for notification type
  const getNotificationIcon = (tipo: string) => {
    switch (tipo) {
      case 'estoque_baixo':
        return <Package size={16} className="text-orange-500" />
      case 'insumo_indisponivel':
        return <AlertTriangle size={16} className="text-red-500" />
      case 'cotacao_pendente':
        return <Clock size={16} className="text-blue-500" />
      case 'entrega_atrasada':
        return <AlertTriangle size={16} className="text-red-500" />
      default:
        return <Bell size={16} className="text-gray-500" />
    }
  }

  // Get priority color
  const getPriorityColor = (prioridade: string) => {
    switch (prioridade) {
      case 'critica':
        return 'border-l-red-500'
      case 'alta':
        return 'border-l-orange-500'
      case 'media':
        return 'border-l-yellow-500'
      case 'baixa':
        return 'border-l-green-500'
      default:
        return 'border-l-gray-500'
    }
  }

  // Handle notification click
  const handleNotificationClick = async (notificacao: any) => {
    if (!notificacao.lida) {
      await markNotificationAsRead(notificacao._id)
    }
  }

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (minutes < 60) {
      return `${minutes}m atrás`
    } else if (hours < 24) {
      return `${hours}h atrás`
    } else {
      return `${days}d atrás`
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell size={20} />
        {unreadNotifications.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadNotifications.length > 9 ? '9+' : unreadNotifications.length}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50 max-h-96 overflow-y-auto">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Notificações - Compras</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              </div>
              {unreadNotifications.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {unreadNotifications.length} não lida(s)
                </p>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto">
              {notificacoes.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <Bell size={24} className="mx-auto mb-2 opacity-50" />
                  <p>Nenhuma notificação</p>
                </div>
              ) : (
                notificacoes.map((notificacao) => (
                  <div
                    key={notificacao._id}
                    onClick={() => handleNotificationClick(notificacao)}
                    className={`p-4 border-b border-l-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      getPriorityColor(notificacao.prioridade)
                    } ${!notificacao.lida ? 'bg-blue-50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notificacao.tipo)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <h4 className={`text-sm font-medium ${
                            !notificacao.lida ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notificacao.titulo}
                          </h4>
                          {!notificacao.lida && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2 mt-1"></div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notificacao.mensagem}
                        </p>
                        <div className="flex justify-between items-center mt-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            notificacao.prioridade === 'critica' ? 'bg-red-100 text-red-800' :
                            notificacao.prioridade === 'alta' ? 'bg-orange-100 text-orange-800' :
                            notificacao.prioridade === 'media' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {notificacao.prioridade.charAt(0).toUpperCase() + notificacao.prioridade.slice(1)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(notificacao.dataNotificacao)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {notificacoes.length > 0 && (
              <div className="p-3 border-t bg-gray-50">
                <button className="text-sm text-blue-600 hover:text-blue-800 w-full text-center">
                  Ver todas as notificações
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default NotificationBellCompras
